import * as Sentry from '@sentry/react';

const SENSITIVE_KEYS = [
  'password',
  'secret',
  'token',
  'apiKey',
  'api_key',
  'authorization',
  'cookie',
  'creditCard',
  'credit_card',
  'ssn',
];

function scrubSensitiveData(data) {
  if (!data || typeof data !== 'object') {
    return data;
  }

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      const lowerKey = key.toLowerCase();
      const isSensitive = SENSITIVE_KEYS.some((sk) =>
        lowerKey.includes(sk.toLowerCase())
      );

      if (isSensitive) {
        return [key, '[Filtered]'];
      }

      if (typeof value === 'object' && value !== null) {
        return [key, scrubSensitiveData(value)];
      }

      return [key, value];
    })
  );
}

const isProduction = import.meta.env.MODE === 'production';

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    if (!isProduction) {
      console.warn('[Sentry] VITE_SENTRY_DSN is not set. Sentry will not be initialized.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || '0.1.0',
    enabled: isProduction,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring
    tracesSampleRate: isProduction ? 0.1 : 1.0,

    // Session replay: only on error
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Do not send events in development
    beforeSend(event) {
      if (!isProduction) {
        return null;
      }

      // Scrub sensitive data from request body
      if (event.request?.data) {
        event.request.data = scrubSensitiveData(event.request.data);
      }

      // Scrub sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            return { ...breadcrumb, data: scrubSensitiveData(breadcrumb.data) };
          }
          return breadcrumb;
        });
      }

      // Scrub sensitive data from extra context
      if (event.extra) {
        event.extra = scrubSensitiveData(event.extra);
      }

      return event;
    },

    // Filter out noisy errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'AbortError',
    ],

    // Limit breadcrumbs
    maxBreadcrumbs: 50,
  });
}

/**
 * Set user context when a user logs in.
 * Call with null to clear context on logout.
 */
export function setSentryUser(user) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name || undefined,
    });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
